import type {
    AlterationDto,
    Dimension,
    Fit,
    GarmentType,
    MeasurementDto,
    ProductDetailDto,
    Size,
    SizeChartDto,
} from '@services';

export default class ProductUtilities {
    static THRESHOLD_KEY_DELIMITER = '$#';

    static ShouldRecommend(fit: Fit, bodyMeasurements: MeasurementDto[] | null) {
        if (!fit.enabled) {
            return false;
        }

        // Ensure measurements satisfy min and max constraints
        return fit.dimensions?.every(
            (dimension) => this.IsDimensionMinimumSatisfied(fit, dimension, bodyMeasurements)
                && this.IsDimensionMaximumSatisfied(fit, dimension, bodyMeasurements),
        );
    }

    static GetRecommendedSize(fit: Fit, bodyMeasurements: MeasurementDto[] | null) {
        // Find the size that satisfies thresholds
        return fit.sizes?.find(
            (size) => fit.dimensions?.every(
                (dimension) => this.IsDimensionThresholdSatisfied(fit, dimension, size.label!, bodyMeasurements),
            ),
        );
    }

    static GetSizeChartTemplateCsv(productCode: string, unitOfMeasurement: string): string {
        return [
            `ProductCode,${productCode},unit,${unitOfMeasurement},Sizing cutoff,Endpoint`,
            'Fit,SampleM,Alteration dimension,altDimension,Alteration threshold,99,Alteration margin,99,Alt fits,M|B|Penguin,Enabled,false',
            ',Offset,Minimum,Maximum,2XS,XS,S,M,L,XL,XXL,3XL,4XL,5XL,6XL',
            'dimension1,1,50,,870,970,1020,1070,1120,1220,1270,1320,1370,1420,1470',
            'dimension2,2,50,,870,970,1020,1070,1120,1220,1270,1320,1370,1420,1470',
            'FitRequirement:bustToHip,,1.2,1.5',
            `FitRequirement:heightRange,,${this.ScaleSampleFromMillimetres(1600, unitOfMeasurement)},${this.ScaleSampleFromMillimetres(1800, unitOfMeasurement)}`,
        ].join('\r\n');
    }

    private static ScaleSampleFromMillimetres(millimetres: number, unitOfMeasurement: string): number {
        switch (unitOfMeasurement.toLowerCase()) {
            case 'cm':
                return millimetres / 10;
            case 'in':
                return Math.round(millimetres / 25.4);
            default:
                return millimetres;
        }
    }

    static GetAlteration(fit: Fit, bodyMeasurements: MeasurementDto[], sizeLabel: string): AlterationDto | null {
        if (!sizeLabel || !fit.alterations || fit.alterations.length === 0) {
            return null;
        }

        const alteration = fit.alterations[0];
        const bodyMeasurement = bodyMeasurements.find((m) => m.key === alteration.dimension)?.value;
        const sizeThreshold = this.GetThreshold(fit, sizeLabel, alteration.dimension!);

        if (bodyMeasurement == null || sizeThreshold == null) {
            return null;
        }

        const difference = sizeThreshold - bodyMeasurement;
        if (difference < (alteration?.threshold ?? 0)) {
            return null;
        }

        const alteredSize = bodyMeasurement + (alteration.margin ?? 35);
        const alterationAmount = sizeThreshold - alteredSize;

        return {
            amount: Math.round(alterationAmount),
            target: Math.round(alteredSize),
            dimension: alteration.dimension,
        };
    }

    static IsDimensionThresholdSatisfied(fit: Fit, dimension: Dimension, sizeLabel: string, bodyMeasurements: MeasurementDto[] | null): boolean {
        const measurement = bodyMeasurements?.find((m) => m.key === dimension.code);
        if (!measurement) {
            return false;
        }
        const adjustedMeasurement = measurement.value! - (dimension.offset ?? 0);
        if (adjustedMeasurement <= this.GetThreshold(fit, sizeLabel, dimension.code!)) {
            return true;
        }
        return false;
    }

    static IsDimensionMinimumSatisfied(fit: Fit, dimension: Dimension, bodyMeasurements: MeasurementDto[] | null): boolean {
        const measurement = bodyMeasurements?.find((m) => m.key === dimension.code);
        if (!measurement) {
            return false;
        }
        // No longer apply offset to measurement for minimum check
        return measurement.value! >= (dimension.minimum ?? 0);
    }

    static IsDimensionMaximumSatisfied(fit: Fit, dimension: Dimension, bodyMeasurements: MeasurementDto[] | null): boolean {
        // If there's no maximum set, we assume it's satisfied
        if (dimension.maximum == null) {
            return true;
        }

        const measurement = bodyMeasurements?.find((m) => m.key === dimension.code);
        if (!measurement) {
            return false;
        }
        // No longer apply offset to measurement for maximum check
        return measurement.value! <= dimension.maximum!;
    }

    static GetAllSizeLabels(product?: ProductDetailDto): string[] {
        if (!product || !product.sizing || !product.sizing.fits) {
            return [];
        }
        const sizes: string[] = product!.sizing!.fits!.reduce((a, c) => {
            c.sizes!.forEach((s) => a.push(s.label!));
            return a;
        }, [] as string[]);

        return Array.from(new Set(sizes));
    }

    static GetFit(fits: Fit[] | null, fitName: string) {
        return fits?.length === 1 ? fits[0] : fits!.find((f) => f.name === fitName) ?? null;
    }

    static GetSizeLabels(fit?: Fit): string[] {
        const sizes = (fit?.sizes ?? []);
        return sizes.map((s) => s.label!);
    }

    static GetSizes(fit?: Fit): Size[] {
        return (fit?.sizes ?? []);
    }

    static ConvertToCsv(sizeChart: SizeChartDto) {
        const rows: string[][] = [];
        rows.push(['ProductCode', sizeChart.productCode!]);

        sizeChart.fits!.forEach((f) => {
            const dimensions = f.dimensions ?? [];
            const sizes = ProductUtilities.GetSizes(f);
            // fit header row
            const fitHeader = ['Fit', f.name!];
            if (!f.enabled) {
                fitHeader.push('Enabled');
                fitHeader.push('false');
            }
            if (f.alternativeFitCriteria) {
                fitHeader.push('Alt fits');
                fitHeader.push(f.alternativeFitCriteria);
            }
            if (f.alterations && f.alterations.length > 0) {
                f.alterations?.forEach((a) => {
                    fitHeader.push('Alteration dimension');
                    fitHeader.push(a.dimension!);
                    fitHeader.push('Alteration threshold');
                    fitHeader.push(a.threshold?.toString() ?? '0');
                    fitHeader.push('Alteration margin');
                    fitHeader.push(a.margin?.toString() ?? '0');
                });
            }
            rows.push(fitHeader);
            // size row
            const labels = ['', 'Offset', 'Minimum', 'Maximum']; // size row has an empty cell in first column, followed by offset, minimum, and maximum
            sizes.forEach((s) => labels.push(s.label ?? ''));
            rows.push(labels);

            // optional size code row
            if (sizes.some((s) => s.code)) {
                const sizeCodeRow = ['SizeCode', '', '', '']; // the SizeCode row has no offset, minimum, or maximum
                sizes.forEach((s) => sizeCodeRow.push(s.code ?? ''));
                rows.push(sizeCodeRow);
            }

            dimensions.forEach((d) => {
                const values: string[] = [d.code!];
                const offset = d.offset ?? 0;
                values.push(offset.toString());
                const { minimum } = d;
                if (minimum !== undefined && minimum !== null) {
                    values.push(minimum.toString());
                } else {
                    values.push('');
                }
                const { maximum } = d;
                if (maximum !== undefined && maximum !== null) {
                    values.push(maximum.toString());
                } else {
                    values.push('');
                }
                sizes.forEach((s) => {
                    const threshold = this.GetThreshold(f, s.label!, d.code!);
                    values.push(`${threshold}`);
                });
                rows.push(values);
            });

            // We are using the same format as dimensions for fit requirements, but with a FitRequirement: prefix
            const fitRequirements = f.fitRequirements ?? [];
            fitRequirements.forEach((r) => {
                const fitRequirementValues: string[] = [`FitRequirement:${r.code}`];
                fitRequirementValues.push(''); // No offset for fit requirements
                if (r.minimum !== undefined && r.minimum !== null) {
                    fitRequirementValues.push(r.minimum.toString());
                } else {
                    fitRequirementValues.push('');
                }
                if (r.maximum !== undefined && r.maximum !== null) {
                    fitRequirementValues.push(r.maximum.toString());
                } else {
                    fitRequirementValues.push('');
                }
                // No size thresholds for fit requirements
                rows.push(fitRequirementValues);
            });
        });
        return rows.map((e) => e.join(',')).join('\r\n');
    }

    // Removes offsets from threshold values
    static RemoveOffsetFromFit(fit: Fit): Fit {
        const updatedFit = JSON.parse(JSON.stringify(fit)) as Fit;

        if (updatedFit.thresholds) {
            Object.keys(updatedFit.thresholds).forEach((key) => {
                const dimensionCode = (key.split(this.THRESHOLD_KEY_DELIMITER))[1];
                const offset = updatedFit.dimensions?.find((d) => d.code === dimensionCode)?.offset ?? 0;
                updatedFit.thresholds![key] -= offset;
            });
        }

        updatedFit.dimensions = updatedFit.dimensions?.map((dimension) => ({
            ...dimension,
        }));

        return updatedFit;
    }

    // Removes offsets from minimum and threshold values
    static RemoveOffsetsFromSizeChart(sizeChart: SizeChartDto): SizeChartDto {
        const updatedSizeChart = JSON.parse(JSON.stringify(sizeChart)) as SizeChartDto;
        updatedSizeChart.fits = updatedSizeChart.fits?.map((fit) => this.RemoveOffsetFromFit(fit)) ?? [];
        return updatedSizeChart;
    }

    // Updates offset for fits that have had offsets removed from minimum and threshold values
    static UpdateOffset(fit: Fit, dimensionIndex: number, millimeters: number): Fit {
        const updatedFit = JSON.parse(JSON.stringify(fit)) as Fit;
        updatedFit.dimensions![dimensionIndex].offset = millimeters;
        return updatedFit;
    }

    // Updates minimum for fits
    static UpdateMinimum(fit: Fit, dimensionIndex: number, millimeters: number): Fit {
        const updatedFit = JSON.parse(JSON.stringify(fit)) as Fit;
        updatedFit.dimensions![dimensionIndex].minimum = millimeters;
        return updatedFit;
    }

    // Updates maximum for fits
    static UpdateMaximum(fit: Fit, dimensionIndex: number, millimeters: number | null): Fit {
        const updatedFit = JSON.parse(JSON.stringify(fit)) as Fit;
        updatedFit.dimensions![dimensionIndex].maximum = millimeters;
        return updatedFit;
    }

    static GetThreshold(fit: Fit, sizeLabel: string, dimensionCode: string): number {
        const key = `${sizeLabel}${this.THRESHOLD_KEY_DELIMITER}${dimensionCode}`;
        return fit.thresholds![key] ?? 0;
    }

    static UpdateThreshold(fit: Fit, sizeLabel: string, dimensionCode: string, value: number) {
        const key = `${sizeLabel}${this.THRESHOLD_KEY_DELIMITER}${dimensionCode}`;
        fit.thresholds![key] = value;
    }

    static HasMissingAlterationDimension(sizeChart: SizeChartDto): boolean {
        return sizeChart.fits?.some((fit) => fit.alterations?.some((alt) => !fit.dimensions?.some((d) => d.code === alt.dimension))) ?? false;
    }

    static IsChartValid(sizeChart: SizeChartDto): boolean {
        if (ProductUtilities.HasMissingAlterationDimension(sizeChart)) return false;
        return true;
    }

    static ChangeDimensionCode(fit: Fit, dimensionIndex: number, dimensionCodeTo: string) {
        const updatedFit = JSON.parse(JSON.stringify(fit)) as Fit;
        const dimensionCodeFrom = updatedFit.dimensions![dimensionIndex].code;
        if (updatedFit.thresholds) {
            Object.keys(updatedFit.thresholds).forEach((key) => {
                const [sizeLabel, dimensionCode] = key.split(this.THRESHOLD_KEY_DELIMITER);
                if (dimensionCode === dimensionCodeFrom) {
                    const newKey = `${sizeLabel}${this.THRESHOLD_KEY_DELIMITER}${dimensionCodeTo}`;
                    updatedFit.thresholds![newKey] = updatedFit.thresholds![key];
                    delete updatedFit.thresholds![key];
                }
            });
        }
        updatedFit.dimensions![dimensionIndex].code = dimensionCodeTo;
        return updatedFit;
    }
}

// TODO: this should be auto-generated \ replaced with translation...
export const allGarmentTypes: { [key in GarmentType]: string } = {
    Shirt: 'Shirt',
    TShirt: 'TShirt',
    Outerwear: 'Outerwear',
    Shorts: 'Shorts',
    Trousers: 'Trousers',
    Skirt: 'Skirt',
    FullBody: 'FullBody',
    Footwear: 'Footwear',
    Belt: 'Belt',
    Gloves: 'Gloves',
    Accessories: 'Accessories',
    Jumper: 'Jumper',
    Helmet: 'Helmet',
    BodyArmour: 'BodyArmour',
    Other: 'Other',
};
